import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AxiosError } from 'axios';

import { showSuccessToast, showWarningToast } from '@/components/ui/sonner';
import {
  type SaleSetupData,
  saleSetupSchema,
} from '@/features/original-sale/schemas/sale-setup.schema';
import {
  submitSaleSetupReq,
  updateSaleApplicationReq,
} from '@/features/original-sale/api/sale.requests';

interface UseSaleSetupFormProps {
  postId?: string;
  initialPrice?: number;
  initialFreeChaptersCount?: number;
  applicationStatus?: string;
  onSuccess?: () => void;
}

export const useSaleSetupForm = ({
  postId,
  initialPrice,
  initialFreeChaptersCount,
  applicationStatus,
  onSuccess,
}: UseSaleSetupFormProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const hasNewExistingApplication = applicationStatus === 'new';
  const hasCompletedApplication = applicationStatus === 'completed';

  const form = useForm<SaleSetupData>({
    resolver: zodResolver(saleSetupSchema),
    mode: 'onChange',
    defaultValues: {
      price: initialPrice,
      freeChaptersCount: initialFreeChaptersCount ?? 1,
    },
  });

  useEffect(() => {
    if (initialPrice !== undefined) {
      form.setValue('price', initialPrice);
    }
    if (initialFreeChaptersCount !== undefined) {
      form.setValue('freeChaptersCount', initialFreeChaptersCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrice, initialFreeChaptersCount]);

  const onSubmit = async (data: SaleSetupData) => {
    if (!postId) {
      showWarningToast('ID произведения не найден');
      return;
    }

    try {
      if ((hasNewExistingApplication || hasCompletedApplication) && isEditing) {
        await updateSaleApplicationReq(postId, {
          price: data.price,
          freeChaptersCount: data.freeChaptersCount,
        });

        showSuccessToast('Заявка успешно обновлена');
        setIsEditing(false);
      } else {
        await submitSaleSetupReq(postId, {
          price: data.price,
          freeChaptersCount: data.freeChaptersCount,
        });

        showSuccessToast('Продажи успешно запущены');
        form.reset();
      }

      onSuccess?.();
    } catch (error) {
      console.error('Sale setup submission error:', error);

      let errorMessage =
        (hasNewExistingApplication || hasCompletedApplication) && isEditing
          ? 'Не удалось обновить заявку.'
          : 'Не удалось запустить продажи.';

      if (error instanceof AxiosError && error.response?.data?.message) {
        errorMessage += ` ${error.response.data.message}`;
      } else if (error instanceof Error && error.message) {
        errorMessage += ` ${error.message}`;
      }

      showWarningToast(errorMessage);
    }
  };

  const toggleEditMode = () => {
    setIsEditing((prev) => {
      return !prev;
    });
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    onSubmitDirect: onSubmit,
    isSubmitting: form.formState.isSubmitting,
    isValid: form.formState.isValid,
    isEditing,
    toggleEditMode,
    hasNewExistingApplication,
    hasCompletedApplication,
  };
};
